import React, { useEffect, useState, useContext, useRef } from 'react';
import { View, TextInput, Pressable } from 'react-native';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faXmark,
  faToggleOn,
  faToggleOff,
} from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../contexts/UserContext';
import { createPortal } from 'react-dom';

import { useNavigate } from 'app/src/utils/routing';
import { Text } from 'app/src/styles';
import * as colors from 'app/src/styles/colors';

import { SEARCH } from './queries';
import Result from './Result';

const INITIAL_LIMIT = 10;
const LOAD_MORE_STEP = 20;
const DEBOUNCE_MS = 250;

const Container = styled(View)`
  flex: 1;
  flex-direction: row;
  background-color: white;
  align-self: ${props => props.screenWidth < 600 ? 'left' : 'center'};
  align-items: center;
  height: 32px;
  padding-left: 8px;
  padding-right: 8px;
  margin-top: 12px;
  margin-left: ${props => props.screenWidth < 600 ? '20px' : '80px'};
  margin-right: ${props => props.screenWidth < 600 ? '20px' : '40px'};
  border-width: 2px;
  border-style: solid;
  border-color: ${props => props.focused ? colors.highlight : 'black'};
  max-width: 600px;
`;

const Input = styled(TextInput)`
  outline-width: 0px;
  outline-color: white;
  flex: 1
  margin-left: 12px;
`;

const Results = styled(View)`
  position: absolute;
  top: ${props => props.screenWidth < 600 ? '120px' : props.screenWidth < 992 ? '60px' : '60px'};
  left: ${props => props.screenWidth < 600 ? '0px' : props.screenWidth < 992 ? '160px' : '242px'};
  background-color: white;
  border-width: 1px;
  border-style: solid;
  border-color: black;
  padding-top: 5px;
`;

const StatusText = styled(Text)`
  margin: 5px;
  color: ${colors.disabled};
`;

const ToggleRow = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  column-gap: 6px;
  margin: 5px;
  padding-top: 5px;
  border-top-width: 1px;
  border-top-color: ${colors.disabled};
  border-top-style: solid;
`;

const LoadMoreRow = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  column-gap: 6px;
  margin: 5px;
`;

const LoadMoreText = styled(Text)`
  color: ${colors.highlight};
  font-weight: 600;
`;

const Search = () => {
  const { screenWidth } = useContext(UserContext);
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  // Displayed results are held locally so a "Load more" refetch keeps the
  // current list on screen; a fresh search clears it back to null.
  const [searchResults, setSearchResults] = useState(null);
  const containerRef = useRef(null);
  const resultsRef = useRef(null);
  // The keyup listener is bound once and reads the latest results and selected
  // index from refs. Consecutive key presses can arrive before React re-renders,
  // so both refs are written synchronously at their source (onCompleted below
  // and updateSelectedIndex) rather than mirrored in a post-render effect.
  const searchResultsRef = useRef(null);
  const selectedIndexRef = useRef();
  const navigate = useNavigate();

  const setResults = (value) => {
    searchResultsRef.current = value;
    setSearchResults(value);
  };

  const updateSelectedIndex = (value) => {
    selectedIndexRef.current = value;
    setSelectedIndex(value);
  };
  const [getResults, { loading }] = useLazyQuery(SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: data => setResults(data.search),
  });

  // Debounce the typed term so we query once the user pauses, not on every
  // keystroke. Toggling inactive or loading more fetches immediately (below),
  // since those change includeInactive/limit rather than the term.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(term), DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [term]);

  useEffect(() => {
    if (debouncedTerm && debouncedTerm.length > 2) {
      getResults({ variables: { term: debouncedTerm, includeInactive, limit } });
    }
  }, [debouncedTerm, includeInactive, limit]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (resultsRef.current?.contains(e.target)) return;

      setIsOpen(false);
    };

    window.document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyUp = (e) => {
      const items = searchResultsRef.current?.results;
      if (!items) {
        return;
      }

      if (0 === items.length) {
        updateSelectedIndex(undefined);
        return;
      }

      const currentIndex = selectedIndexRef.current;
      let newIndex = currentIndex;
      if ('undefined' === typeof newIndex) {
        newIndex = -1;
      }

      if (e.keyCode === 13) { // Enter
        if ('undefined' === typeof currentIndex) {
          return false;
        }

        const result = items[currentIndex];
        if (!result) {
          return false;
        }

        const typename = result.__typename;

        setTerm('');
        if ('Asset' === typename) {
          navigate(`/sellers/${result.seller.id}/assets/${result.id}`);
        } else if ('Buyer' === typename) {
          navigate(`/buyers/${result.id}`);
        } else if ('BuyerUser' === typename) {
          navigate(`/buyers/${result.buyer.id}`);
        } else if ('BuyerSale' === typename) {
          navigate(`/buyers/${result.buyer.id}`);
        } else if ('SaleAsset' === typename) {
          navigate(`/sellers/${result.asset.seller.id}/assets/${result.asset.id}`);
        } else if ('Sale' === typename) {
          navigate(`/sales/${result.id}`);
        } else if ('Seller' === typename) {
          navigate(`/sellers/${result.id}`);
        } else if ('Vendor' === typename) {
          navigate(`/vendors/${result.id}`);
        }
      } else if (38 === e.keyCode) { // Up
        newIndex = newIndex - 1;
      } else if (40 === e.keyCode) { // Down
        newIndex = newIndex + 1;
      } else {
        updateSelectedIndex(undefined);
        return;
      }

      if (newIndex < 0) {
        updateSelectedIndex(0);
      } else if (newIndex >= items.length) {
        updateSelectedIndex(items.length - 1);
      } else {
        updateSelectedIndex(newIndex);
      }
    }

    window.document.addEventListener('keyup', handleKeyUp);

    return () => {
      window.document.removeEventListener('keyup', handleKeyUp);
    }
  }, []);

  return (
    <Container ref={containerRef} screenWidth={screenWidth} focused={isFocused}>
      {term ? (
        <Pressable onPress={() => setTerm('')}>
          <FontAwesomeIcon
            icon={faXmark}
            color={colors.disabled}
          />
        </Pressable>
      ) : (
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          color={colors.disabled}
        />
      )}
      <Input
        placeholder="Search"
        placeholderTextColor={colors.disabled}
        value={term}
        onFocus={() => { setIsFocused(true); setIsOpen(true); }}
        onBlur={() => setIsFocused(false)}
        onChange={e => {
          setTerm(e.target.value);
          setLimit(INITIAL_LIMIT);
          setResults(null);
          updateSelectedIndex(undefined);
          setIsOpen(true);
        }}
      />
      {isOpen && term.length > 2 && (
        createPortal(
          <Results ref={resultsRef} screenWidth={screenWidth}>
            {!searchResults ? (
              <StatusText>Searching…</StatusText>
            ) : 0 === searchResults.results.length ? (
              <StatusText>No results</StatusText>
            ) : (
              <>
                {searchResults.results.map((result, index) => (
                  <Result
                    key={`${result.__typename}-${result.id}`}
                    result={result}
                    setTerm={setTerm}
                    selected={index === selectedIndex}
                  />
                ))}
                <StatusText>
                  {`Showing ${searchResults.results.length} of ${searchResults.total}`}
                </StatusText>
                {loading ? (
                  <StatusText>Loading more…</StatusText>
                ) : searchResults.results.length < searchResults.total && (
                  <LoadMoreRow onPress={() => setLimit(limit + LOAD_MORE_STEP)}>
                    <LoadMoreText>Load more</LoadMoreText>
                  </LoadMoreRow>
                )}
              </>
            )}
            <ToggleRow
              onPress={() => {
                setIncludeInactive(!includeInactive);
                setLimit(INITIAL_LIMIT);
                setResults(null);
                updateSelectedIndex(undefined);
              }}
            >
              <FontAwesomeIcon
                icon={includeInactive ? faToggleOn : faToggleOff}
                color={includeInactive ? colors.highlight : colors.disabled}
              />
              <Text>Include inactive assets</Text>
            </ToggleRow>
          </Results>, document.body
        )
      )}
    </Container>
  );
};

export default Search;
